import { useTranslation } from "react-i18next";
import { useState } from "react";
import { AlertTriangle, Clapperboard, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/primitives/Button";
import { isSingleProject, type DirectProject } from "@/lib/directApi";
import { formatCredits } from "@/lib/creditsApi";


// Analysis outcome: SINGLE_CLIP / MULTI_CLIP summary + cost + the ONE confirmation for the whole batch.
export function PlanPanel({
  project,
  onGenerate,
  onReanalyze,
  busy,
}: {
  project: DirectProject;
  onGenerate: () => Promise<void>;
  onReanalyze: () => Promise<void>;
  busy: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const cost = t("credits.amount", { n: formatCredits(project.credits_required ?? 0, i18n.language) });
  const { status, plan, estimate, max_clips: maxClips } = project;
  const clips = plan?.clips ?? [];
  const n = clips.length;
  const seconds = plan?.estimated_total_duration_seconds ?? 0;

  if (status === "ANALYZING") {
    return (
      <div data-testid="plan-analyzing" className="flex items-center gap-3 text-sm text-[#7FD4FF]">
        <Loader2 className="h-4 w-4 animate-spin" /> {t("direct.analyzing")}
      </div>
    );
  }
  if (status === "DRAFT" || status === "ANALYSIS_FAILED") {
    return (
      <div data-testid="plan-draft" className="space-y-4">
        <p className={status === "ANALYSIS_FAILED" ? "text-sm text-[#FF8A8A]" : "text-sm text-muted-foreground"}>
          {status === "ANALYSIS_FAILED" ? t("direct.analysisFailedMsg") : t("direct.draftMsg")}
        </p>
        <Button data-testid="plan-analyze-btn" disabled={busy} onClick={onReanalyze}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {t("direct.analyzeCta")}
        </Button>
      </div>
    );
  }
  if (status === "TOO_MANY_CLIPS") {
    return (
      <div data-testid="plan-too-many" className="space-y-4">
        <p className="flex items-start gap-2 text-sm text-[#FFC773]">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {t("direct.tooManyMsg", { max: maxClips })}
        </p>
        <Button variant="secondary" data-testid="plan-reanalyze-btn" disabled={busy} onClick={onReanalyze}>
          <RefreshCw className="h-4 w-4" /> {t("direct.reanalyze")}
        </Button>
      </div>
    );
  }
  if (status !== "PLANNED" || !plan) return null;

  const single = plan.mode === "single_clip";
  const v4 = isSingleProject(project);
  return (
    <div data-testid="plan-panel" className="space-y-6">
      <p data-testid="plan-mode-message" className="max-w-2xl text-sm text-foreground sm:text-base">
        {single ? t("direct.singleMsg") : t("direct.multiMsg")}
      </p>

      <dl className="grid gap-4 text-sm sm:grid-cols-3">
        <div className="glass rounded-xl px-4 py-3 !shadow-none">
          <dt className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">{t("direct.mode")}</dt>
          <dd data-testid="plan-mode" className="mt-1 font-display text-lg font-bold uppercase tracking-wide text-[#7FD4FF]">
            {single ? t("direct.modeSingle") : t("direct.modeMulti", { n })}
          </dd>
        </div>
        <div className="glass rounded-xl px-4 py-3 !shadow-none">
          <dt className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            {v4 ? t("direct.optimizedDuration") : single ? t("direct.targetDuration") : t("direct.totalDuration")}
          </dt>
          <dd data-testid="plan-duration" className="mt-1 font-display text-lg font-bold uppercase tracking-wide text-foreground">~{seconds} s</dd>
        </div>
        <div className="glass rounded-xl px-4 py-3 !shadow-none">
          <dt className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">{t("credits.cost")}</dt>
          <dd data-testid="plan-cost" className="mt-1 font-display text-lg font-bold uppercase tracking-wide text-foreground">
            {cost}
            {estimate.anchor_usd > 0 && <span className="ml-2 text-[10px] font-medium normal-case tracking-normal text-muted-foreground">({t(v4 ? "direct.includesAnchorAlways" : "direct.includesAnchor")})</span>}
          </dd>
        </div>
      </dl>

      {!single && (
        <ul data-testid="plan-clip-list" className="grid gap-2 text-sm sm:grid-cols-2">
          {clips.map((c) => (
            <li key={c.index} data-testid={`plan-clip-${c.index}`} className="flex items-center justify-between gap-3 rounded-lg border border-[#2E7BFF]/20 bg-[#05070D]/60 px-4 py-2.5">
              <span className="font-display font-semibold uppercase tracking-wide text-foreground">{t("direct.clipLine", { n: c.index, s: c.duration_target_seconds })}</span>
              <span className="truncate text-xs text-muted-foreground">{c.title}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-col gap-4 border-t border-[#2E7BFF]/15 pt-6 sm:flex-row sm:items-center sm:justify-between">
        {v4 ? (
          <span className="space-y-1 text-xs text-muted-foreground">
            <span data-testid="plan-duration-note" className="block text-[#3DDC84]">{t("direct.autoDurationNote")}</span>
            <span className="block">{t("direct.autoDurationNote2")}</span>
          </span>
        ) : (
          <button type="button" data-testid="plan-reanalyze-link" disabled={busy} onClick={onReanalyze} className="text-left text-xs text-muted-foreground underline-offset-4 hover:text-[#7FD4FF] hover:underline disabled:opacity-40">
            {t("direct.reanalyze")}
          </button>
        )}
        <button type="button" data-testid="plan-generate-btn" disabled={busy} onClick={() => setConfirming(true)} className="cta-yellow h-12 px-8 text-sm sm:text-base">
          <span className="inline-flex items-center gap-2.5">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Clapperboard className="h-5 w-5" />}
            {single ? t("direct.generateOne") : t("direct.generateMany", { n })}
          </span>
        </button>
      </div>

      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setConfirming(false)}>
          <div className="glass-panel w-full max-w-md rounded-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="plan-confirm">
            <h3 className="font-display text-base font-semibold uppercase tracking-wide text-foreground">{t("direct.confirmTitle")}</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {single
                ? t("direct.confirmBodyOne", { seconds, cost })
                : t("direct.confirmBody", { n, seconds, cost })}{" "}
              {t("common.startsAfterConfirm")}
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setConfirming(false)} data-testid="plan-confirm-cancel">{t("common.cancel")}</Button>
              <Button size="sm" data-testid="plan-confirm-accept" onClick={async () => { setConfirming(false); await onGenerate(); }}>
                {t("common.confirmGenerate")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
